// PATH: src/pages/admin/tabs/AlbumsTab.jsx
// VERSION: 2.2 — Added existing items display with delete functionality

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Flex, Input, Table, Thead, Tbody, Tr, Th, Td, useToast, Heading, Spinner, Textarea, Text, SimpleGrid,
  useColorModeValue, Image, IconButton, Wrap, WrapItem, Tooltip, Badge,
} from '@chakra-ui/react';
import { FiRefreshCw, FiPlusCircle, FiEdit2, FiTrash2, FiSave, FiUploadCloud, FiX } from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import CloudinaryImageField from '../../../admin/components/CloudinaryImageField';
import CloudinaryMultiImageField from '../../../admin/components/CloudinaryMultiImageField';
import { cld } from '../../../utils/cloudinary';

export default function AlbumsTab() {
  const toast = useToast();

  // --- Theme Variables ---
  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const sectionHeadingColor = useColorModeValue('brand.700', 'text.default');
  const batchTextColor = useColorModeValue('gray.600', 'text.muted');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('brand.200', 'whiteAlpha.300');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('brand.300', 'whiteAlpha.300');
  const tableBorderColor = useColorModeValue('brand.200', 'whiteAlpha.300');
  const itemThumbBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  // --- BW Style Object (Dynamic) ---
  const bw = {
    box: {
      border: '1px solid',
      borderColor: borderColor,
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: inputBorder,
      bg: inputBg,
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'whiteAlpha.400') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: 'transparent',
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'whiteAlpha.300'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.100'),
        borderColor: useColorModeValue('brand.400', 'whiteAlpha.400'),
      },
    },
  };

  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [form, setForm] = useState({
    id: null,
    title: '',
    date: '',
    eventKey: '',
    cover: '',
    tags: '',
  });

  // Lote de ítems para agregar al álbum seleccionado (cuando form.id existe)
  const [itemsBatch, setItemsBatch] = useState([]);
  // Ítems existentes del álbum (cargados al editar)
  const [existingItems, setExistingItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/albums', { params: { includeItems: 0, page: 1, limit: 200 } });
      setAlbums(data?.items || data || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando álbumes' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Cargar ítems existentes de un álbum
  const loadExistingItems = useCallback(async (albumId) => {
    if (!albumId) {
      setExistingItems([]);
      return;
    }
    setLoadingItems(true);
    try {
      const { data } = await axiosInstance.get(`/albums/${albumId}/items`);
      setExistingItems(data?.items || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando ítems del álbum' });
      setExistingItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [toast]);

  // Eliminar un ítem del álbum
  async function removeItem(albumId, itemId) {
    if (!window.confirm('¿Eliminar este ítem del álbum?')) return;
    setDeletingItemId(itemId);
    try {
      await axiosInstance.delete(`/albums/${albumId}/items/${itemId}`);
      toast({ status: 'success', title: 'Ítem eliminado' });
      // Actualizar lista local
      setExistingItems((prev) => prev.filter((it) => it.id !== itemId));
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar el ítem' });
    } finally {
      setDeletingItemId(null);
    }
  }

  function editRow(r) {
    setForm({
      id: r.id,
      title: r.title || '',
      date: r.date || '',
      eventKey: r.eventKey || '',
      cover: r.cover || '',
      tags: Array.isArray(r.tags) ? r.tags.join(', ') : '',
    });
    setItemsBatch([]); // limpiar batch al cambiar de álbum
    loadExistingItems(r.id); // cargar ítems existentes
  }

  function resetForm() {
    setForm({ id: null, title: '', date: '', eventKey: '', cover: '', tags: '' });
    setItemsBatch([]);
    setExistingItems([]);
  }

  async function save() {
    const payload = {
      title: form.title.trim(),
      date: form.date || null,
      eventKey: form.eventKey || null,
      cover: form.cover || null, // publicId o URL
      tags: (form.tags || '').split(',').map((s) => s.trim()).filter(Boolean),
    };
    if (!payload.title) {
      toast({ status: 'warning', title: 'Título requerido' });
      return;
    }
    try {
      if (form.id) {
        await axiosInstance.patch(`/albums/${form.id}`, payload);
        toast({ status: 'success', title: 'Álbum actualizado' });
      } else {
        await axiosInstance.post('/albums', payload);
        toast({ status: 'success', title: 'Álbum creado' });
      }
      resetForm();
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar álbum?')) return;
    try {
      await axiosInstance.delete(`/albums/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  async function saveItemsBatch() {
    if (!form.id) {
      toast({ status: 'warning', title: 'Selecciona/edita un álbum primero' });
      return;
    }
    if (!itemsBatch.length) {
      toast({ status: 'warning', title: 'No hay archivos en el lote' });
      return;
    }
    setLoading(true);
    try {
      for (const it of itemsBatch) {
        await axiosInstance.post(`/albums/${form.id}/items`, {
          title: it.title || 'Sin título',
          type: it.type === 'video' ? 'video' : 'image',
          src: it.publicId, // publicId Cloudinary
          thumbnail: null,
          tags: [],
        });
      }
      toast({ status: 'success', title: `Se agregaron ${itemsBatch.length} ítems al álbum` });
      setItemsBatch([]);
      loadExistingItems(form.id); // refrescar lista de ítems existentes
    } catch {
      toast({ status: 'error', title: 'Error guardando ítems del álbum' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>Álbumes</Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Crea y organiza colecciones de fotos/videos. Puedes subir una portada y luego cargar múltiples ítems.
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} mb={2}>
        <Input {...bw.input} placeholder="Título" value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        <Input {...bw.input} placeholder="Fecha (YYYY-MM-DD)" value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
        <Input {...bw.input} placeholder="EventKey (opcional)" value={form.eventKey}
          onChange={(e) => setForm((f) => ({ ...f, eventKey: e.target.value }))} />
      </SimpleGrid>

      <CloudinaryImageField
        label="Portada (Cloudinary)"
        value={form.cover}
        onChange={(v) => setForm((f) => ({ ...f, cover: v }))}
        folder="albums/covers"
        previewW={420}
        previewH={240}
      />

      <Textarea
        {...bw.input}
        rows={2}
        placeholder="Tags (coma separada)"
        value={form.tags}
        onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
        my={3}
      />

      <Flex gap={2} my={3} wrap="wrap">
        <Button {...bw.btn} leftIcon={form.id ? <FiSave /> : <FiPlusCircle />} onClick={save}>
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && <Button {...bw.btnGhost} onClick={resetForm}>Cancelar</Button>}
        <Button {...bw.btnGhost} leftIcon={<FiRefreshCw />} onClick={load}>Refrescar</Button>
      </Flex>

      {form.id && (
        <Box mt={4}>
          {/* Sección: Ítems existentes */}
          <Heading fontSize="md" mb={2} color={sectionHeadingColor}>
            Ítems existentes del álbum
            {existingItems.length > 0 && (
              <Badge ml={2} colorScheme="blue" fontSize="sm" verticalAlign="middle">
                {existingItems.length}
              </Badge>
            )}
          </Heading>

          {loadingItems ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" mr={2} />
              <Text fontSize="sm" color={batchTextColor}>Cargando ítems...</Text>
            </Flex>
          ) : existingItems.length === 0 ? (
            <Text fontSize="sm" color={batchTextColor} mb={4}>
              Este álbum no tiene ítems todavía.
            </Text>
          ) : (
            <Wrap spacing={3} mb={4}>
              {existingItems.map((item) => {
                const isDeleting = deletingItemId === item.id;
                // Generar thumbnail
                const thumbUrl = item.thumbnail
                  || (item.src && !item.src.startsWith('http') ? cld(item.src, { w: 120, h: 80, crop: 'fill', gravity: 'auto' }) : item.src)
                  || '';

                return (
                  <WrapItem key={item.id}>
                    <Box
                      position="relative"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={borderColor}
                      bg={itemThumbBg}
                      opacity={isDeleting ? 0.5 : 1}
                    >
                      <Image
                        src={thumbUrl}
                        alt={item.caption || `Item ${item.id}`}
                        w="120px"
                        h="80px"
                        objectFit="cover"
                        fallback={
                          <Flex w="120px" h="80px" bg="red.100" align="center" justify="center">
                            <Text fontSize="xs" color="red.600" textAlign="center" px={1}>
                              Sin imagen
                            </Text>
                          </Flex>
                        }
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="width:120px;height:80px;display:flex;align-items:center;justify-content:center;background:#fed7d7;color:#c53030;font-size:11px;text-align:center;padding:4px;">Error al cargar</div>';
                        }}
                      />
                      <Tooltip label="Eliminar ítem" placement="top">
                        <IconButton
                          icon={isDeleting ? <Spinner size="xs" /> : <FiX />}
                          size="xs"
                          colorScheme="red"
                          position="absolute"
                          top={1}
                          right={1}
                          borderRadius="full"
                          onClick={() => removeItem(form.id, item.id)}
                          isDisabled={isDeleting}
                          aria-label="Eliminar ítem"
                        />
                      </Tooltip>
                      {item.type === 'video' && (
                        <Badge
                          position="absolute"
                          bottom={1}
                          left={1}
                          colorScheme="purple"
                          fontSize="9px"
                        >
                          VIDEO
                        </Badge>
                      )}
                    </Box>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}

          {/* Sección: Carga masiva */}
          <Heading fontSize="md" mb={2} mt={4} color={sectionHeadingColor}>
            Agregar nuevos ítems (carga masiva)
          </Heading>
          <CloudinaryMultiImageField
            label={`Sube imágenes/videos para el álbum "${form.title || form.id}"`}
            value={itemsBatch}
            onChange={setItemsBatch}
            folderBase="albums"
            albumTitle={form.title || String(form.id)}
          />
          <Flex gap={2} mt={3} wrap="wrap" align="center">
            <Button {...bw.btn} leftIcon={<FiUploadCloud />} onClick={saveItemsBatch} isDisabled={!itemsBatch.length}>
              Agregar al álbum
            </Button>
            <Text fontSize="sm" color={batchTextColor}>En lote: <b>{itemsBatch.length}</b></Text>
          </Flex>
        </Box>
      )}

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto" mt={4}>
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: tableBorderColor } }}
            border="1px solid"
            borderColor={tableBorderColor}
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor={tableBorderColor}>ID</Th>
                <Th borderColor={tableBorderColor}>Título</Th>
                <Th borderColor={tableBorderColor}>Fecha</Th>
                <Th borderColor={tableBorderColor}>Tags</Th>
                <Th borderColor={tableBorderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {albums.map((a) => (
                <Tr key={a.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor={tableBorderColor}>{a.id}</Td>
                  <Td borderColor={tableBorderColor}>{a.title}</Td>
                  <Td borderColor={tableBorderColor}>{a.date || ''}</Td>
                  <Td borderColor={tableBorderColor}>{Array.isArray(a.tags) ? a.tags.join(', ') : ''}</Td>
                  <Td borderColor={tableBorderColor}>
                    <Button {...bw.btnGhost} size="xs" mr={2} leftIcon={<FiEdit2 />} onClick={() => editRow(a)}>
                      Editar
                    </Button>
                    <Button {...bw.btnGhost} size="xs" leftIcon={<FiTrash2 />} onClick={() => remove(a.id)}>
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {albums.length === 0 && (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={4} borderColor={tableBorderColor}>
                    No hay álbumes registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
